/**
 * Mester-AI dictation parser.
 *
 * Turns a spoken Danish job description into priced quote lines. The parser is
 * deliberately rule based and runs locally, so dictation works on site without
 * a network connection. Material names are matched against the material
 * catalogue maintained under Materialer.
 */

export interface MaterialPreset {
  id: string
  name: string
  price?: number
}

export type QuoteLineKind = 'labour' | 'material'

export interface QuoteLine {
  id: string
  kind: QuoteLineKind
  /** What the parser heard, cleaned up. */
  description: string
  quantity: number
  unit: string
  unitPrice: number
  /** True when no price could be resolved and the user must fill it in. */
  needsPrice: boolean
  /** The clause this line was derived from, shown as provenance. */
  source: string
}

export interface QuoteBreakdown {
  lines: QuoteLine[]
  labourHours: number
  labourTotal: number
  materialTotal: number
  subtotal: number
  vat: number
  total: number
  /** Clauses the parser could not turn into a line. */
  unparsed: string[]
}

const NUMBER_WORDS: Record<string, number> = {
  en: 1,
  et: 1,
  to: 2,
  tre: 3,
  fire: 4,
  fem: 5,
  seks: 6,
  syv: 7,
  otte: 8,
  ni: 9,
  ti: 10,
  elleve: 11,
  tolv: 12,
  femten: 15,
  tyve: 20,
  tredive: 30,
  fyrre: 40,
  halvanden: 1.5,
}

const UNIT_ALIASES: Record<string, string> = {
  meter: 'meter',
  m: 'meter',
  mtr: 'meter',
  stk: 'stk',
  styk: 'stk',
  stykker: 'stk',
  rulle: 'rulle',
  ruller: 'rulle',
  pakke: 'pakke',
  pakker: 'pakke',
  sæt: 'sæt',
  poser: 'pose',
  pose: 'pose',
}

const FILLER = new Set([
  'skal',
  'der',
  'jeg',
  'vi',
  'med',
  'af',
  'på',
  'til',
  'og',
  'samt',
  'en',
  'et',
  'the',
  'ca',
  'cirka',
  'omkring',
  'bruges',
  'bruge',
  'brugt',
  'monteres',
  'montere',
  'sættes',
  'sætte',
  'laves',
  'lave',
  'trækkes',
  'trække',
  'skiftes',
  'skifte',
  'opsættes',
  'nye',
  'ny',
  'nyt',
  'stykke',
])

const HOUR_PATTERN = /\btim(?:e|er)\b/i

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
}

/** Splits dictation into clauses on sentence and list separators. */
function splitClauses(input: string): string[] {
  return input
    .split(/[.,;\n]|\bog\b|\bsamt\b|\bderefter\b|\bplus\b/i)
    .map((clause) => clause.trim())
    .filter((clause) => clause.length > 1)
}

/** Reads the leading quantity from a clause, as digits or a Danish number word. */
function readQuantity(tokens: string[]): number | null {
  if (/^en\s+halv$/i.test(tokens.slice(0, 2).join(' '))) return 0.5
  for (const token of tokens) {
    const digits = token.replace(',', '.').replace(/[^0-9.]/g, '')
    if (digits && !Number.isNaN(parseFloat(digits))) return parseFloat(digits)
    const word = NUMBER_WORDS[normalize(token)]
    if (word !== undefined) return word
  }
  return null
}

function readUnit(tokens: string[]): string | null {
  for (const token of tokens) {
    const unit = UNIT_ALIASES[normalize(token.replace(/[^a-zæøå]/gi, ''))]
    if (unit) return unit
  }
  return null
}

/** Strips numbers, units and filler words so only the subject remains. */
function readSubject(clause: string): string {
  const words = clause
    .split(/\s+/)
    .map((word) => word.replace(/[^0-9a-zæøåA-ZÆØÅ,.-]/g, ''))
    .filter((word) => word.length > 0)
    .filter((word) => !/^[0-9]/.test(word))
    .filter((word) => NUMBER_WORDS[normalize(word)] === undefined)
    .filter((word) => !UNIT_ALIASES[normalize(word)])
    .filter((word) => !FILLER.has(normalize(word)))
  return words.join(' ').trim()
}

/** Scores how well a preset name matches a subject, by shared word stems. */
function scorePreset(subject: string, presetName: string): number {
  const subjectWords = normalize(subject)
    .split(/\s+/)
    .filter((w) => w.length > 2)
  const presetWords = normalize(presetName)
    .split(/\s+/)
    .filter((w) => w.length > 2)
  if (subjectWords.length === 0 || presetWords.length === 0) return 0

  let score = 0
  presetWords.forEach((presetWord) => {
    if (
      subjectWords.some(
        (subjectWord) =>
          subjectWord.includes(presetWord) || presetWord.includes(subjectWord),
      )
    ) {
      score += 1
    }
  })
  return score / presetWords.length
}

function findPreset(
  subject: string,
  presets: MaterialPreset[],
): MaterialPreset | null {
  let best: MaterialPreset | null = null
  let bestScore = 0
  presets.forEach((preset) => {
    const score = scorePreset(subject, preset.name)
    if (score > bestScore) {
      bestScore = score
      best = preset
    }
  })
  return bestScore >= 0.5 ? best : null
}

interface ParseOptions {
  transcript: string
  hourlyRate: number
  presets: MaterialPreset[]
  vatRate?: number
}

export function parseDictation({
  transcript,
  hourlyRate,
  presets,
  vatRate = 0.25,
}: ParseOptions): QuoteBreakdown {
  const lines: QuoteLine[] = []
  const unparsed: string[] = []

  splitClauses(transcript).forEach((clause, index) => {
    const tokens = clause.split(/\s+/)
    const quantity = readQuantity(tokens)
    const subject = readSubject(clause)

    if (HOUR_PATTERN.test(clause)) {
      const hours = quantity ?? 1
      lines.push({
        id: `line-${index}-labour`,
        kind: 'labour',
        description: subject ? `Arbejdstid — ${subject}` : 'Arbejdstid',
        quantity: hours,
        unit: 'timer',
        unitPrice: hourlyRate,
        needsPrice: hourlyRate <= 0,
        source: clause,
      })
      return
    }

    if (!subject) {
      unparsed.push(clause)
      return
    }

    const preset = findPreset(subject, presets)
    lines.push({
      id: `line-${index}-material`,
      kind: 'material',
      description: preset ? preset.name : subject,
      quantity: quantity ?? 1,
      unit: readUnit(tokens) ?? 'stk',
      unitPrice: preset?.price ?? 0,
      needsPrice: !preset || preset.price === undefined || preset.price === 0,
      source: clause,
    })
  })

  return summarize(lines, unparsed, vatRate)
}

/** Recomputes totals after the user edits a line. */
export function summarize(
  lines: QuoteLine[],
  unparsed: string[] = [],
  vatRate = 0.25,
): QuoteBreakdown {
  const lineTotal = (line: QuoteLine) => line.quantity * line.unitPrice
  const labourLines = lines.filter((line) => line.kind === 'labour')
  const materialLines = lines.filter((line) => line.kind === 'material')

  const labourTotal = labourLines.reduce((sum, l) => sum + lineTotal(l), 0)
  const materialTotal = materialLines.reduce((sum, l) => sum + lineTotal(l), 0)
  const subtotal = labourTotal + materialTotal

  return {
    lines,
    labourHours: labourLines.reduce((sum, l) => sum + l.quantity, 0),
    labourTotal,
    materialTotal,
    subtotal,
    vat: subtotal * vatRate,
    total: subtotal * (1 + vatRate),
    unparsed,
  }
}

export function lineTotal(line: QuoteLine): number {
  return line.quantity * line.unitPrice
}
